<template>
    <FullscreenWizard
        :current-step-index
        :error
        :fetching
        :step-count="progressIndicatorStepCount"
        :step-current="progressIndicatorStepCurrent"
        :step-title
        class="questionnaire"
        no-close-on-esc
        @close="close"
        @previous="back"
    >
        <template
            v-if="questionnaire"
            #content
        >
            <div class="step-wrapper">
                <transition
                    :mode="stepTransitionMode"
                    :name="stepTransitionName"
                >
                    <PageErrorFourHundredFour
                        v-if="!id || !stepsConditional.length"
                    />

                    <QuestionnaireAuth
                        v-else-if="isOnLoginBeforeStartingScreen"
                        :errors="createCustomerErrors"
                        :fetching-new-customer-submit="createCustomerFetching"
                        :sign-up-step="questionnaire.signUpStep"
                        @submit-new-customer="createCustomer"
                    />

                    <QuestionnaireStep
                        v-else-if="
                            currentStepIndex !== 'complete' &&
                            currentStep &&
                            currentStepContentConditional
                        "
                        :key="currentStepIndex"
                        :content-conditional="currentStepContentConditional"
                        :step="currentStep"
                        :step-index="currentStepIndex"
                        class="questionnaire-step"
                    />

                    <QuestionnaireStepFinal
                        v-else-if="currentStepIndex === 'complete'"
                        key="signup"
                        :questionnaire
                        :sign-up-step="questionnaire.signUpStep"
                        class="questionnaire-step-signup"
                        @submit="submit"
                    />
                </transition>
            </div>
        </template>

        <template
            v-if="!isOnLoginBeforeStartingScreen"
            #footer
        >
            <ThButton
                v-if="!isComplete"
                :color="'var(--slate)'"
                :disabled="isNextDisabled"
                :type="'bordered'"
                fixed-width
                @click="cta"
            >
                Next
            </ThButton>
        </template>
    </FullscreenWizard>
</template>

<script lang="ts" setup>
import { computed, ref, toRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import { useRouter } from 'vue-router'
import { onKeyUp } from '@vueuse/core'

import { useTranslator } from '@/i18n'
import { useRegionPlugin } from '@/plugins/region'
import { type MagicLinkRouteParams, useRoutes } from '@/router/routes'

import { useCreateCustomerThroughQuestionnaireWithoutQuestionnaireMutation } from '@/graphql/api/mutations/createCustomerThroughQuestionnaireWithoutQuestionnaire.mutation.generated'
import useQuestionnaireNavigation from '@/components/cms/component/questionnaire/useQuestionnaireNavigation'
import { useQuestionnaireTracking } from '@/components/cms/component/questionnaire/useQuestionnaireTracking'
import { useFullscreenWizardTransition } from '@/components/layout/modal/fullscreen-wizard/useFullscreenWizardTransition'
import { useCloseFullscreenWizard } from '@/components/layout/modal/fullscreen-wizard/useCloseFullscreenWizard'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useQuestionnaireQuery } from '@/graphql/api/questionnaire/questionnaire.query.generated.ts'
import { useAuth } from '@/plugins/auth'
import { useLastResponseErrorAtPath } from '@/helpers/errors'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { useIsAdmin } from '@/components/app/isAdmin'

import QuestionnaireStep from '@/components/cms/component/questionnaire/QuestionnaireStep.vue'
import QuestionnaireStepFinal from '@/components/cms/component/questionnaire/QuestionnaireStepFinal.vue'
import FullscreenWizard from '@/components/layout/modal/fullscreen-wizard/FullscreenWizard.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import PageErrorFourHundredFour from '@/components/PageErrorFourHundredFour.vue'
import QuestionnaireAuth from '@/components/cms/component/questionnaire/QuestionnaireAuth.vue'

const props = defineProps<{
    id: string
    magicLink?: MagicLinkRouteParams
}>()

const { isLogged, setAuth } = useAuth()

const redirect = useRedirect()
const routes = useRoutes()
const router = useRouter()

if (props.magicLink) {
    const returnRoute = router.resolve(router.currentRoute.value)

    returnRoute.query['user'] = null
    returnRoute.query['expires'] = null
    returnRoute.query['hash'] = null

    const returnUrl = router.resolve(returnRoute).href

    const magicLink = router.resolve(
        routes.profile.magicLink(props.magicLink, returnUrl)
    ).href

    await redirect(magicLink)
}

const { locale } = useTranslator()
const currency = useCurrencyInput()
const { regionCode } = useRegionPlugin()

const variables = computed(() => ({
    id: props.id,
    locale: locale.value,
    currency: currency.value,
    region: regionCode.value,
}))

const response = useQuestionnaireQuery({ variables })
const { data, error: responseError, fetching } = response
const error = toRef(() => responseError.value?.graphQLErrors[0]?.message)
const questionnaire = toRef(() => data.value?.questionnaire)
const requireLoginBeforeStarting = toRef(
    () => questionnaire.value?.requireLoginBeforeStarting
)

const { close: closeWizard } = useCloseFullscreenWizard()
const close = () => {
    questionnaireExit()

    closeWizard()
}

const steps = toRef(() => questionnaire.value?.steps || [])
const isSubmitted = ref(false)
const {
    stepsConditional,
    currentStepIndex,
    currentStep,
    currentStepContentConditional,
    isComplete,
    isNextDisabled,
    progressIndicatorStepCount,
    progressIndicatorStepCurrent,
    incrementStep,
    goBack,
    isOnLoginBeforeStartingScreen,
} = useQuestionnaireNavigation(
    steps,
    toRef(() => props.id),
    isSubmitted,
    requireLoginBeforeStarting,
    isLogged
)

const { navigationHookApp, stepCompletedLast, questionnaireExit } =
    useQuestionnaireTracking(
        questionnaire,
        currentStepContentConditional,
        progressIndicatorStepCount,
        currentStepIndex
    )

const submit = async () => {
    isSubmitted.value = true

    stepCompletedLast()
}

const cta = () => {
    if (isNextDisabled.value) {
        return
    }

    navigationHookApp('Next')
    incrementStep()
}

onKeyUp('Enter', cta)

const back = () => {
    navigationHookApp('app_back')
    goBack()
}

const stepTitle = computed(() =>
    isOnLoginBeforeStartingScreen.value
        ? ''
        : currentStepIndex.value === 'complete'
          ? 'Sign up & save'
          : currentStep.value?.title
)

const { stepTransitionName, stepTransitionMode } =
    useFullscreenWizardTransition(currentStepIndex)

const isAdmin = useIsAdmin()

const responseCreateCustomer =
    useCreateCustomerThroughQuestionnaireWithoutQuestionnaireMutation()

const {
    executeMutation: executeMutationCreateCustomer,
    fetching: createCustomerFetching,
} = responseCreateCustomer

const createCustomerErrors = useLastResponseErrorAtPath([
    {
        response: responseCreateCustomer,
        path: 'createCustomerThroughQuestionnaireWithoutQuestionnaire',
    },
])

const { customerRegistrationInput } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const createCustomer = async (successCallback: () => void) => {
    const customer = customerRegistrationInput.value

    if (!customer || !questionnaire.value) {
        return
    }

    await executeMutationCreateCustomer({
        input: customer,
        isConsultant: isAdmin.value,
        locale: locale.value,
        type: questionnaire.value.type,
    }).then((response) => {
        const customer =
            response.data
                ?.createCustomerThroughQuestionnaireWithoutQuestionnaire

        if (!customer) {
            return
        }

        setAuth(customer.login)

        successCallback()
    })
}

await response
</script>

<style
    lang="scss"
    scoped
    src="@/components/layout/modal/fullscreen-wizard/transitions.scss"
></style>

<style lang="scss" scoped>
.questionnaire {
    .step-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;

        .questionnaire-step,
        .questionnaire-step-signup {
            position: absolute;
            width: 100%;
        }
    }
}
</style>
